class StickyAddToCart {
  constructor() {
    this.stickyBar = document.querySelector('.sticky-add-to-cart');
    if (!this.stickyBar) return;

    this.productForm = this.stickyBar.querySelector('product-form');
    this.variantInput = this.stickyBar.querySelector('[name="id"]');
    this.quantityInput = this.stickyBar.querySelector('[name="quantity"]');
    this.submitButton = this.stickyBar.querySelector('.sticky-add-to-cart__button');
    this.priceElement = this.stickyBar.querySelector('.sticky-add-to-cart__price');
    this.productImage = this.stickyBar.querySelector('.sticky-add-to-cart__image img');
    
    this.mainProductForm = document.querySelector('product-form:not(.sticky-add-to-cart product-form)');
    this.mainVariantInput = this.mainProductForm?.querySelector('[name="id"]');
    this.mainQuantityInput = this.mainProductForm?.querySelector('[name="quantity"]');

    this.isVisible = false;
    this.lastScrollY = window.scrollY;
    
    this.init();
  }

  init() {
    this.setupScrollListener();
    this.setupVariantSync();
    this.setupQuantitySync();
    this.setupIntersectionObserver();
    this.addBodyClass();
  }

  addBodyClass() {
    // Add class to body for CSS adjustments
    if (window.innerWidth <= 749) {
      document.body.classList.add('sticky-add-to-cart-active');
    }
  }

  setupScrollListener() {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', () => {
      this.handleResize();
    });
  }

  handleScroll() {
    const currentScrollY = window.scrollY;
    const documentHeight = document.documentElement.scrollHeight - window.innerHeight;
    const scrollPercentage = (currentScrollY / documentHeight) * 100;
    const showThreshold = 30; // Show after scrolling 30% of the page
    const hideThreshold = 25; // Hide when scrolling back to top 25% (with 5% buffer to prevent flickering)

    // Show/hide based on scroll percentage with hysteresis
    if (scrollPercentage >= showThreshold && !this.isVisible) {
      this.show();
    } else if (scrollPercentage <= hideThreshold && this.isVisible) {
      this.hide();
    }

    this.lastScrollY = currentScrollY;
  }

  handleResize() {
    if (window.innerWidth <= 749) {
      document.body.classList.add('sticky-add-to-cart-active');
    } else {
      document.body.classList.remove('sticky-add-to-cart-active');
      if (!this.stickyBar.classList.contains('sticky-add-to-cart--desktop')) {
        this.hide();
      }
    }
  }

  setupIntersectionObserver() {
    // Hide sticky bar when main add to cart button is visible
    const mainAddToCartButton = document.querySelector('.product-form__buttons .btn');
    if (!mainAddToCartButton) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && this.isVisible) {
          // Main add to cart is visible, hide sticky bar
          this.hide();
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '50px'
    });

    observer.observe(mainAddToCartButton);
  }

  setupVariantSync() {
    if (!this.mainVariantInput) return;

    // Sync variant changes from main form to sticky form
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          this.syncVariant();
        }
      });
    });

    observer.observe(this.mainVariantInput, {
      attributes: true,
      attributeFilter: ['value']
    });

    // Also listen for direct value changes
    this.mainVariantInput.addEventListener('change', () => {
      this.syncVariant();
    });

    // Listen for variant changes via PubSub (if available)
    if (window.PUB_SUB_EVENTS?.variantChange) {
      document.addEventListener(window.PUB_SUB_EVENTS.variantChange, (event) => {
        this.handleVariantChange(event.detail);
      });
    }
  }

  setupQuantitySync() {
    if (!this.mainQuantityInput || !this.quantityInput) return;

    // Sync quantity from main form to sticky form
    this.mainQuantityInput.addEventListener('change', () => {
      this.quantityInput.value = this.mainQuantityInput.value;
    });

    // Sync quantity from sticky form to main form
    this.quantityInput.addEventListener('change', () => {
      this.mainQuantityInput.value = this.quantityInput.value;
    });
  }

  syncVariant() {
    if (!this.mainVariantInput || !this.variantInput) return;

    const selectedVariantId = this.mainVariantInput.value;
    this.variantInput.value = selectedVariantId;

    // Update button state based on variant availability
    this.updateButtonState();
    
    // Update price and image if variant data is available
    this.updateVariantInfo();
  }

  handleVariantChange(variant) {
    if (!variant) return;

    this.variantInput.value = variant.id;
    this.updateButtonState();
    this.updatePrice(variant);
    this.updateImage(variant);
  }

  updateButtonState() {
    const variantId = this.variantInput.value;
    if (!variantId || !window.productVariants) return;

    const variant = window.productVariants.find(v => v.id == variantId);
    
    if (variant && variant.available) {
      this.submitButton.disabled = false;
      this.submitButton.querySelector('span').textContent = this.submitButton.dataset.addToCartText || 'Add to cart';
    } else {
      this.submitButton.disabled = true;
      this.submitButton.querySelector('span').textContent = window.variantStrings?.soldOut || 'Sold out';
    }
  }

  updateVariantInfo() {
    const variantId = this.variantInput.value;
    if (!variantId || !window.productVariants) return;

    const variant = window.productVariants.find(v => v.id == variantId);
    if (!variant) return;

    this.updatePrice(variant);
    this.updateImage(variant);
  }

  updatePrice(variant) {
    if (!this.priceElement || !variant) return;

    const priceContainer = this.priceElement.querySelector('.price');
    if (!priceContainer) return;

    let priceHTML = '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      // Sale price
      priceHTML = `
        <span class="price__regular">
          <span class="visually-hidden">Regular price</span>
          <span class="price-item price-item--regular">${this.formatMoney(variant.compare_at_price)}</span>
        </span>
        <span class="price__sale">
          <span class="visually-hidden">Sale price</span>
          <span class="price-item price-item--sale price-item--last">${this.formatMoney(variant.price)}</span>
        </span>
      `;
    } else {
      // Regular price
      priceHTML = `
        <span class="price__regular">
          <span class="visually-hidden">Regular price</span>
          <span class="price-item price-item--regular">${this.formatMoney(variant.price)}</span>
        </span>
      `;
    }

    priceContainer.innerHTML = priceHTML;
  }

  updateImage(variant) {
    if (!this.productImage || !variant || !variant.featured_media) return;

    const imageUrl = variant.featured_media.preview_image.src.replace(/\.(jpg|jpeg|gif|png|bmp|bitmap|tiff|tif)(\?.*)?$/i, '_60x60$1$2');
    this.productImage.src = imageUrl;
    this.productImage.alt = variant.featured_media.alt || '';
  }

  formatMoney(cents) {
    // Basic money formatting - you might want to use your theme's money formatting function
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    
    // Fallback formatting
    const dollars = (cents / 100).toFixed(2);
    return `$${dollars}`;
  }

  show() {
    if (this.isVisible) return;
    
    this.stickyBar.classList.add('is-visible');
    this.isVisible = true;
    
    // Dispatch custom event
    this.stickyBar.dispatchEvent(new CustomEvent('sticky-add-to-cart:show'));
  }

  hide() {
    if (!this.isVisible) return;
    
    this.stickyBar.classList.remove('is-visible');
    this.isVisible = false;
    
    // Dispatch custom event
    this.stickyBar.dispatchEvent(new CustomEvent('sticky-add-to-cart:hide'));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new StickyAddToCart();
  });
} else {
  new StickyAddToCart();
}

// Handle quantity input component
if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', class QuantityInput extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('input');
      this.changeEvent = new Event('change', { bubbles: true });
      this.input.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', this.onButtonClick.bind(this))
      );
    }

    onInputChange() {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      const previousValue = this.input.value;

      if (event.target.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      if (isNaN(value) || value < 1) {
        this.input.value = 1;
      }
    }
  });
}
